import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Layout } from "Layout";

import { CustomFields } from "components/CustomFields/CustomFields";
import { EditModalHeader } from "components/EditModalHeader";
import { FrmFieldGrid } from "components/FrmFieldGrid";
import { ObjectEditDialogContent } from "components/ObjectEditDialogContent";
import { QuickAddCustomer } from "components/QuickAddCustomer";
import { FrmTextField } from "components/formFields/FrmTextField";
import { Addresses } from "components/formFields/address/sales/Addresses";
import {
  FrmComment,
  FrmDepartment,
  FrmNumber,
  FrmCustomerWithAdd,
  FrmCustomerNotes,
  FrmCustomerMessage,
  FrmChannel,
  FrmShipMethod,
} from "components/formFields/frm";
import { FrmLocation } from "components/formFields/frm/FrmLocation";
import { FrmSignature } from "components/formFields/frm/FrmSignature";
import { DatePicker } from "components/html/Input/DatePicker";
import { DateTimePicker } from "components/html/Input/DateTimePicker";
import { FixedLoadingIndicator } from "components/utility/FixedLoadingIndicator";
import { FormErrors } from "components/utility/FormErrors";
import { Loading } from "components/utility/Loading";
import { VSpace } from "components/utility/VSpace";
import { WarningModal } from "components/utility/WarningModal";

import { i18n } from "services/i18nService";
import {
  updateTotals,
  ZERO_TOTALS,
} from "services/sosInventoryService/moreTransaction/updateTotals";
import { setPageDirty } from "services/utility/edit";

import { useClasses } from "hooks/useClasses";
import { useCustomFieldDefsAsObject } from "hooks/useCustomFieldDefinitions";
import { useCustomers } from "hooks/useCustomers";
import { useErrors } from "hooks/useErrors";
import { useItems } from "hooks/useItems";
import { useLineHandler } from "hooks/useLineHandler";
import { useTaxCodes, TAX_CODE_TYPE_SALES } from "hooks/useTaxCodes";

import { LineItems } from "views/Rentals/Rental/LineItems";
import {
  getTransaction,
  updateTransaction,
} from "views/Rentals/Rental/controller";

import { openAlert } from "globalState/alertSlice";
import {
  editModalLoadingIndicatorOn,
  editModalLoadingIndicatorOff,
} from "globalState/loadingSlice";

import { theme } from "SosTheme";
import { OBJECT_TYPES, ITEM_QUICKLIST_CASES } from "appConstants";
import { EMPTY_LINE_ITEM } from "editConfig";

const OBJECT_TYPE = OBJECT_TYPES.RENTAL.fullString;
const CUSTOM_FIELD_OBJECT_TYPES = [
  OBJECT_TYPE,
  OBJECT_TYPES.CUSTOMER.fullString,
];

export function Rental(props) {
  const { id, onClose, newFromId, newFromObjectType } = props;

  // TRANSACTION STATE
  const [record, setRecord] = useState();
  const [lines, lineHandler] = useLineHandler();
  const [relatedRecords, setRelatedRecords] = useState({});

  const CURRENT_STATE = { record, lines, relatedRecords };

  // UI STATE
  const { errors, setErrors, isInError } = useErrors();
  const [clearLines, setClearLines] = useState(false);
  const customFieldDefs = useCustomFieldDefsAsObject(CUSTOM_FIELD_OBJECT_TYPES);
  const [addNewCustomer, setAddNewCustomer] = useState(false);
  const [newCustomerName, setNewCustomerName] = useState("");
  const [customerAdded, setCustomerAdded] = useState(0);
  const [itemAdded, setItemAdded] = useState(0);
  const [totals, setTotals] = useState(ZERO_TOTALS);
  const loadingState = useSelector((state) => state.loading.editModal);

  // UPDATERS FOR CONTROLLER TO UPDATE STATE
  const UPDATERS = useMemo(
    () => ({
      setRecord,
      lineHandler,
      setRelatedRecords,
      setErrors,
    }),
    [lineHandler, setErrors]
  );

  // RELATED RECORD LISTS

  const taxCodes = useTaxCodes(TAX_CODE_TYPE_SALES);
  const { classes, classesCount } = useClasses();
  const customers = useCustomers(customerAdded);
  const { items, itemsCount } = useItems(
    ITEM_QUICKLIST_CASES.NON_CATEGORY,
    itemAdded
  );

  // SETTINGS
  const departmentEnabled = useSelector(
    (state) => state.userCompanySettings.settings.departmentEnabled
  );

  // OTHER SETUP
  const dispatch = useDispatch();

  // LOAD INITIAL TRANSACTION STATE
  useEffect(() => {
    async function _getTransaction() {
      dispatch(editModalLoadingIndicatorOn());
      await getTransaction(
        id,
        newFromId,
        newFromObjectType,
        UPDATERS,
        customFieldDefs[OBJECT_TYPE]
      );
      dispatch(editModalLoadingIndicatorOff());
    }
    if (customFieldDefs) {
      _getTransaction();
    }
  }, [
    dispatch,
    id,
    newFromId,
    newFromObjectType,
    lineHandler,
    UPDATERS,
    customFieldDefs,
  ]);

  // HANDLE ALL USER INPUT
  function handleInputFieldChange(field, newValue) {
    setPageDirty();
    updateTransaction(
      field,
      newValue,
      customFieldDefs[OBJECT_TYPE],
      CURRENT_STATE,
      UPDATERS
    );
  }

  // ANCILLARY UI FUNCTIONS

  // update totals whenever the line items change
  useEffect(() => updateTotals(lines, setTotals), [lines]);

  // show alert if customer is on credit hold
  useEffect(() => {
    if (relatedRecords.customer?.creditHold) {
      dispatch(
        openAlert({ type: "error", message: i18n("customer.CreditHold") })
      );
    }
  }, [relatedRecords.customer, dispatch]);

  async function handleAddNewCustomer(customer) {
    setPageDirty();
    handleInputFieldChange("customer", customer);
    setCustomerAdded((prev) => prev + 1);
    setAddNewCustomer(false);
    return;
  }

  // clear lines
  function doClearLines() {
    setPageDirty();
    lineHandler({ type: "clear", blankLine: EMPTY_LINE_ITEM[OBJECT_TYPE] });
    setClearLines(false);
  }

  return (
    <>
      <EditModalHeader
        record={record}
        setRecord={setRecord}
        setReloadRecord={setRecord}
        objectType={OBJECT_TYPE}
        relatedRecords={relatedRecords}
        lines={lines}
        setErrors={setErrors}
        errors={errors}
        text={record?.number}
        handleClose={onClose}
        customerId={record?.customer?.id}
      />
      {loadingState && <Loading />}
      <ObjectEditDialogContent>
        <Layout
          pageTitle={
            id
              ? `${i18n("objectType.rental.Sentence")} ${record?.number || ""}`
              : i18n("objectType.rental.New")
          }
        >
          <div style={{ margin: theme.spacing(2) }}>
            <FormErrors errors={errors} setErrors={setErrors} />
            {record ? (
              <>
                <FrmFieldGrid gridAutoFlow="dense">
                  <div style={{ gridColumn: "1 / 2" }}>
                    <DateTimePicker
                      name="date"
                      value={record.date}
                      label={i18n("frmLabel.DateTime")}
                      onValueChange={handleInputFieldChange}
                      error={isInError("date")}
                      dataTesting="date"
                    />
                  </div>
                  <div style={{ gridColumn: "2 / 2" }}>
                    <FrmNumber
                      objectType={OBJECT_TYPE}
                      recordId={record.id}
                      label={i18n("rental.labelNumber")}
                      value={record.number}
                      onValueChange={handleInputFieldChange}
                      error={isInError("number")}
                    />
                  </div>
                  <div style={{ gridColumn: "2 / 2" }}>
                    <FrmLocation
                      name="fromLocation"
                      value={record.fromLocation}
                      label={i18n("frmLabel.FromLocation")}
                      onValueChange={handleInputFieldChange}
                      width="25rem"
                    />
                  </div>
                  <div style={{ gridColumn: "1 / 2" }}>
                    <FrmCustomerWithAdd
                      value={record.customer}
                      onValueChange={handleInputFieldChange}
                      setAddNewCustomer={setAddNewCustomer}
                      setNewCustomerName={setNewCustomerName}
                      error={isInError("customer")}
                      customers={customers}
                    />
                  </div>
                  <div style={{ gridColumn: "2 / 2" }}>
                    <FrmLocation
                      name="toLocation"
                      value={record.toLocation}
                      label={i18n("frmLabel.ToLocation")}
                      onValueChange={handleInputFieldChange}
                      width="25rem"
                    />
                  </div>
                  <div style={{ gridColumn: "1 / 2" }}>
                    <FrmCustomerNotes
                      value={record.customerNotes}
                      onValueChange={handleInputFieldChange}
                    />
                  </div>
                </FrmFieldGrid>
                <VSpace space={1} />
                <Addresses
                  billing={record.billing}
                  shipping={record.shipping}
                  customer={relatedRecords.customer}
                  handleInputFieldChange={handleInputFieldChange}
                  billingLabel={i18n("address.Customer")}
                  shippingLabel={i18n("address.ShipTo")}
                />
                <VSpace space={2} />
                <LineItems
                  record={record}
                  relatedRecords={relatedRecords}
                  objectType={OBJECT_TYPE}
                  lines={lines}
                  taxCodes={taxCodes}
                  items={items}
                  totals={totals}
                  itemsCount={itemsCount}
                  addItem={() => setItemAdded((prev) => prev + 1)}
                  classes={classes}
                  classesCount={classesCount}
                  lineHandler={lineHandler}
                  setClearLines={setClearLines}
                  lineItemsErrors={errors.line}
                />

                <VSpace space={3} />

                <FrmFieldGrid gridAutoFlow="dense">
                  <div style={{ gridColumn: "1 / 2" }}>
                    <FrmShipMethod
                      maxWidth="20rem"
                      onValueChange={handleInputFieldChange}
                      value={record?.shippingMethod}
                    />
                  </div>
                  <div style={{ gridColumn: "1 / 2" }}>
                    <FrmTextField
                      name="trackingNumber"
                      label={i18n("frmLabel.TrackingNumber")}
                      value={record.trackingNumber}
                      onValueBlur={handleInputFieldChange}
                      sx={{ maxWidth: "20rem" }}
                      maxLength={50}
                    />
                  </div>
                  <div style={{ gridColumn: "2 / 2" }}>
                    <FrmChannel
                      maxWidth="20rem"
                      onValueChange={handleInputFieldChange}
                      value={record?.channel}
                    />
                  </div>
                  {departmentEnabled && (
                    <div style={{ gridColumn: "1 / 2" }}>
                      <FrmDepartment
                        maxWidth="20rem"
                        value={record.department}
                        onValueChange={handleInputFieldChange}
                      />
                    </div>
                  )}
                  <div style={{ gridColumn: "2 / 2" }}>
                    <DatePicker
                      name="dueDate"
                      value={record.dueDate}
                      label={i18n("frmLabel.DueDate")}
                      onValueChange={handleInputFieldChange}
                      dataTesting="dueDate"
                    />
                  </div>
                  <div style={{ gridColumn: "1 / 2", maxWidth: "100%" }}>
                    <FrmCustomerMessage
                      value={record.customerMessage}
                      onValueChange={handleInputFieldChange}
                    />
                  </div>
                  <div style={{ gridColumn: "1 / 2" }}>
                    <FrmComment
                      value={record.comment}
                      onValueChange={handleInputFieldChange}
                    />
                  </div>
                  <div style={{ gridColumn: "1 / 2" }}>
                    <FrmSignature
                      objectType={OBJECT_TYPE}
                      id={record.id}
                      hasSignature={record.hasSignature}
                    />
                  </div>
                </FrmFieldGrid>

                <CustomFields
                  customFieldDefinitions={customFieldDefs[OBJECT_TYPE]}
                  customFields={record.customFields}
                  onValueChange={handleInputFieldChange}
                  setErrors={setErrors}
                />
              </>
            ) : (
              !isInError("record") && (
                <FixedLoadingIndicator text={`${i18n("global.Loading")}...`} />
              )
            )}
            <VSpace space={4} />
          </div>
          {record && (
            <>
              {!!addNewCustomer && (
                <QuickAddCustomer
                  open
                  initialNameValue={newCustomerName}
                  onClose={() => setAddNewCustomer(false)}
                  onAdd={handleAddNewCustomer}
                />
              )}
              <WarningModal
                title={i18n("global.ClearLines")}
                message={i18n("global.clearLinesMessage")}
                open={clearLines}
                onOk={doClearLines}
                onClose={() => setClearLines(false)}
              />
            </>
          )}
        </Layout>
      </ObjectEditDialogContent>
    </>
  );
}
